import { useUILock } from "client-util/useUILock";
import { addFlushSockAPIMessagesEffect } from "client-util/util";
import { LargeSpinner } from "components/Loading";
import Globals from "Globals";
import { PuzzlePageWrapper } from "pages/PuzzlePage";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Twemoji from "react-twemoji";
import { DirectBackendReqType } from "sockapi/direct-backend";
import { ScopeType } from "sockapi/scope-types";
import { useNavStore, useRedirectIfNonAdmin } from "stores/NavStore";
import {
  useGlobalScopeState,
  useTeamMembersScopeState,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import { useSockClient } from "stores/ServerInteractionStore";

import "./CertificatePage.css";

function CertificatePage() {
  useRedirectIfNonAdmin();

  const teams = useGlobalScopeState(ScopeType.TEAM_LIST) ?? {};
  const { teamId } = useParams();
  if (teamId === undefined) throw new Error("expect teamId to be defined");
  const team = teams[teamId] ?? null;

  const teamScopeState = useTeamScopeState(teamId);
  const teamMembersScopeState = useTeamMembersScopeState(teamId);
  const areTeamsLoaded = Object.keys(teams).length > 0;
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const sockClient = useSockClient();

  const [emails, setEmails] = useState<string[] | null>(null);
  const getEmailsLock = useUILock();

  useEffect(() => {
    if (!areTeamsLoaded) return;
    if (team !== null) return;

    return addFlushSockAPIMessagesEffect(sockClient, () => {
      Globals.toastr.error(`Team “${teamId}” not found.`);
      setRedirectRoute({ route: "teams" });
    });
  }, [areTeamsLoaded, team, teamId, setRedirectRoute, sockClient]);

  if (
    !areTeamsLoaded ||
    teamId === null ||
    teamMembersScopeState === null ||
    teamScopeState === null
  )
    return <LargeSpinner />;

  const doTheThing = () => {
    const plaintext = document.querySelector(".certificate-plaintext")!;
    const ciphertext = document.querySelector(".certificate-ciphertext")!;
    ciphertext.textContent = "";
    const pieces = Array.from(new Array(12), (_, i) => {
      const copy = plaintext.cloneNode(true) as HTMLDivElement;
      const piece = document.createElement("div");
      copy.style.translate = `${(-(i % 4) * 100) / 4}% ${(-Math.floor(i / 4) * 100) / 3}%`;
      piece.append(copy);
      piece.style.rotate = `${Math.floor(Math.random() * 4) * 90}deg`;
      return piece;
    }).sort(() => Math.random() - 0.5);
    ciphertext.append(...pieces);
  };

  return (
    <PuzzlePageWrapper>
      <div className="centered-page">
        <div className="certificate-plaintext">
          <img className="cert-sia" src="/static/images/cert-sia.png" />
          <img className="cert-laura" src="/static/images/cert-laura.png" />
          <img className="cert-pan" src="/static/images/cert-pan.png" />
          <div className="cert-h1-wrapper">
            <h1>Galactic Puzzle Hunt 2025</h1>
          </div>
          <div className="cert-h2-wrapper">
            <Twemoji tag="h2">{teamScopeState.displayName}</Twemoji>
          </div>
          <div className="cert-h3-wrapper">
            <Twemoji tag="h3">
              {teamMembersScopeState.members.join(", ")}
            </Twemoji>
          </div>
        </div>
        <div className="certificate-toolbar">
          <button onClick={doTheThing}>Split pieces, then...</button>
          <button onClick={print}>Print (letter, landscape, no margin)</button>
          <button
            onClick={() => {
              getEmailsLock.acquireAndRun(async (getIsCanceled) => {
                try {
                  const emails = (
                    await sockClient.sendDirectBackendReqAsync({
                      type: DirectBackendReqType.GET_TEAM_EMAILS,
                      payload: { teamId },
                    })
                  ).payload;
                  if (getIsCanceled()) return;
                  setEmails(emails);
                } catch (err) {
                  console.error(err);
                  toastr.error("Failed to get emails.");
                }
              });
            }}
          >
            Get emails
          </button>
        </div>
        {emails !== null ? (
          emails.length === 0 ? (
            <div>None found.</div>
          ) : (
            <textarea>{emails.join(", ")}</textarea>
          )
        ) : null}
        <div className="certificate-ciphertext" />
      </div>
    </PuzzlePageWrapper>
  );
}

export default CertificatePage;
